import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ProjectRole } from 'src/app/constants/permissions/project-role';
import { CanComponentDeactivate } from '../../helpers/guards/unsaved-changes.guard';
import { ProjectDetails } from '../../data/models/project-details';
import { ProjectConfigurationService } from '../../data/project/project-configuration.service';
import { ToasterService } from '../../api/toaster.service';
import { ProjectsApiService } from '../../api/projects-api.service';
import { ProjectService } from '../../data/project/project.service';
import { COLOR_VARS } from '../../constants/color-vars';
import { ProjectStatusEnum } from '../../constants/project-status';
import { ProjectUserRole } from '../../data/models/project-user';
import { CommonMessages } from '../../constants/common-messages';
import { StringConstants } from '../../constants/string-constants';
import { CancelConfirmationDialogComponent } from '../../components/dialogs/cancel-confirmation-dialog/cancel-confirmation-dialog.component';
import { ScenarioDimension } from 'src/app/data/models/scenario-dimension-details';
import moment from 'moment';
import { SyncService } from '../../api/sync.service';

@Component({
    selector: 'app-project-configuration-summary',
    templateUrl: './project-configuration-summary.component.html',
    styleUrls: ['./project-configuration-summary.component.scss'],
    standalone: false
})
export class ProjectConfigurationSummaryComponent
  implements OnInit, OnDestroy, AfterViewChecked, CanComponentDeactivate
{
  public colorVars: string[] = [];
  stylesObj = { color: 'var(--cobalt-blue)' };
  projectDescription = 'Summary of the project key details.';
  panelOpenState = false;
  subscriptions: Subscription[] = [];
  project: ProjectDetails;
  projectId: any;
  //record count variables
  teamMemberCount: number;
  modulesCount: number;
  scenariosCount: number;
  isSaved = false;
  dataLoaded = false;
  projectTitle: string;
  projectUser: any = [];

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private syncService: SyncService,
    private cdr: ChangeDetectorRef,
    public pcDataService: ProjectConfigurationService,
    public toasterService: ToasterService,
    private projectService: ProjectsApiService,
    private projectDataService: ProjectService
  ) {
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.pcDataService.projectScenarioConfigurationData = this.route.snapshot.data;
  }

  async ngOnInit(): Promise<void> {
    this.colorVars = COLOR_VARS;
    this.projectDataService.projectId = this.projectId;
    const project = await this.projectService.getProjectById(this.projectId);
    Promise.all([project]).then((data) => {
      this.project = data[0];
      this.projectTitle = this.project.projectTitle;
      if (this.project.projectStatus.name === ProjectStatusEnum.ACTIVE.toString()) {
        this.getProjectConfigurationDetails();
      } else {
        const teamMembers = this.pcDataService.projectConfigurationFormGroup.controls['users']?.value || [];
        const initialProjectUsers: any[] = [];
        const color = this.colorVars[Math.floor(Math.random() * this.colorVars.length)];

        this.projectService.getProjectUsers(this.projectId).subscribe((projectUser: ProjectUserRole[]) => {
          projectUser.forEach((user) => {
            let roleName = '';
            let roleId: number = ProjectRole.ProjectAdmin;
            user.projectUserRoles.forEach((role: any) => {
              roleName = role.projectRole.name;
              roleId = role.projectRoleId;
            });
            initialProjectUsers.push({
              firstName: user.firstName,
              lastName: user.lastName,
              alternateEmailAddress: user.email,
              status: user.active,
              roleName: roleName,
              roleId: roleId,
              color: color,
            });
            this.projectDataService.initialAdminEmailId = user.email;
          });
          teamMembers.push(...initialProjectUsers);
          this.pcDataService.projectConfigurationFormGroup.controls['users'].setValue(teamMembers);
          this.teamMemberCount = teamMembers.length;
          const modules = this.pcDataService.projectConfigurationFormGroup.controls['modules'].get('ids')?.value || [];
          this.modulesCount = modules.length;
          const scenarios = this.pcDataService.projectConfigurationFormGroup.controls['scenarios']?.value || [];
          this.scenariosCount = scenarios.length;
          this.dataLoaded = true;
        });
      }
    });
  }

  getProjectConfigurationDetails(): void {
    this.projectService.getProjectConfigurationDetailById(this.projectId).subscribe((res: any) => {
      //solution Offerings
      const solutionOfferings: any = [];
      res.solutionOffering.forEach((s: any) => {
        solutionOfferings.push({
          name: s.name,
          description: s.description,
        });
      });
      this.pcDataService.projectConfigurationFormGroup.controls['solutionOffering'].setValue(solutionOfferings);

      //modules
      const moduleData: any = [];
      res.module.forEach((item: any) => {
        moduleData.push({ id: item.id, description: item.description, name: item.name, sortOrder: item.sortOrder });
      });

      this.pcDataService.projectConfigurationFormGroup.controls['modules'].setValue({ ids: moduleData });
      this.modulesCount = moduleData.length;

      //users
      const userData: any = [];
      res.projectUsers.forEach((user: any) => {
        const color = this.colorVars[Math.floor(Math.random() * this.colorVars.length)];
        userData.push({
          alternateEmailAddress: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          roleName: user.roleName,
          roleId: user.roleId,
          status: user.active,
          color: color,
        });
        this.pcDataService.projectConfigurationFormGroup.controls['users'].setValue(userData);
      });

      //setting the project users count
      this.teamMemberCount = userData?.length;

      //questionnaire
      this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].setValue({
        id: res.questionnaire.questionnaire.id,
        scopeStartDate: res.questionnaire.questionnaire.scopeStartDate,
        scopeEndDate: res.questionnaire.questionnaire.scopeEndDate,
        fiscalEndMonth:
          res.questionnaire.questionnaire.fiscalYearEndMonth + '/' + res.questionnaire.questionnaire.fiscalYearEndDay,
        decimalNumbersToDisplay: res.questionnaire.questionnaire.numberOfDecimalsToDisplay,
        decimalRoundingCalculationDigits: res.questionnaire.questionnaire.decimalRoundingCalculationDigits,
        thresholdAmount: res.questionnaire.questionnaire.thresholdAmountForAccountOutOfBalance,
        overrideAmount: res.questionnaire.questionnaire.overrideAmount,
        amountGlobalTrialBalance: res.questionnaire.questionnaire.amountGlobalInTrailBalance,
        enabledRetainedEarnings: res.questionnaire.questionnaire.enableRetainedEarnings,
        isJeRollForwardEnabled: res.questionnaire.questionnaire.isJeRollForwardEnabled
      });
      this.dataLoaded = true;
      setTimeout(() => {
        this.syncService.jeRollForwardOptionChnage.next(res.questionnaire.questionnaire.isJeRollForwardEnabled)
      });
    });
  }

  confirmExit(): void {
    this.router.navigateByUrl('/dashboard');
    //setting default solutionoffering to sell side
    this.pcDataService.defaultSolutionOffering = 1;
  }

  saveConfiguration(): void {
    //scenarios
    const scenarios: ScenarioDimension[] = [];
    this.pcDataService.projectConfigurationFormGroup.get('scenarios')?.value.forEach((scenario: ScenarioDimension) => {
      scenarios.push(scenario);
    });

    //modules
    const moduleIds: any[] = [];
    this.pcDataService.projectConfigurationFormGroup.controls['modules'].get('ids')?.value.forEach((module: any) => {
      moduleIds.push(module.id);
    });

    //users
    let projectUsers: any[] = [];
    this.pcDataService.projectConfigurationFormGroup.get('users')?.value.forEach((user: any) => {
      projectUsers.push({ userEmail: user.alternateEmailAddress, roleId: user.roleId, isActive: user.status });
    });

    //before saving the project configuration , clear the data of initial project admin
    this.projectDataService.initialAdminEmailId = '';
    projectUsers = projectUsers.filter(
      (user, index, self) => index === self.findIndex((t) => t.userEmail === user.userEmail && t.roleId === user.roleId)
    );
    const data = this.prepareSaveConfigModel(this.projectId, scenarios, moduleIds, projectUsers, true);
    this.projectService.saveProjectConfiguration(data).subscribe((res) => {
      this.isSaved = true;
      this.router.navigateByUrl('/dashboard');
      this.toasterService.info(CommonMessages.CONFIGURATION_COMPLETE, StringConstants.SUCCESS);
      //setting default solutionoffering to sell side
      this.pcDataService.defaultSolutionOffering = 1;
    });
  }

  prepareSaveConfigModel(
    projectId: number,
    scenarios: ScenarioDimension[],
    moduleIds: any[],
    projectUsers: any[],
    isFromConfigurationSummary: boolean
  ): any {
    //questionnaire
    const fiscalEndMonthDay = this.pcDataService.projectConfigurationFormGroup.controls['questionnaire']
      .get('fiscalEndMonth')
      ?.value.split('/');
    const data = {
      projectId: projectId,
      isFromConfigurationSummary: isFromConfigurationSummary,
      solutionOfferingIds: [this.pcDataService.defaultSolutionOffering],
      scenarios: scenarios,
      moduleIds: moduleIds,
      projectUsers: projectUsers,
      projectConfigQuestionnaire: {
        solutionOfferingId: 1,
        moduleId: 1,
        questionnaireId: this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('id')?.value,
        questionnaireSaveModel: {
          scopeStartDate: moment(
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('scopeStartDate')?.value
          ).format('YYYY-MM-DDTHH:mm:ss'),
          scopeEndDate: moment(
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('scopeEndDate')?.value
          ).format('YYYY-MM-DDTHH:mm:ss'),
          fiscalYearEndMonth: fiscalEndMonthDay[0],
          fiscalYearEndDay: fiscalEndMonthDay[1],
          overrideAmount:
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('overrideAmount')?.value,
          amountGlobalInTrailBalance:
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('amountGlobalTrialBalance')
              ?.value,
          enableRetainedEarnings:
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('enabledRetainedEarnings')
              ?.value,
          numberOfDecimalsToDisplay:
            this.pcDataService.projectConfigurationFormGroup.controls['questionnaire'].get('decimalNumbersToDisplay')
              ?.value,
          decimalRoundingCalculationDigits: this.pcDataService.projectConfigurationFormGroup.controls[
            'questionnaire'
          ].get('decimalRoundingCalculationDigits')?.value,
          thresholdAmountForAccountOutOfBalance: this.pcDataService.projectConfigurationFormGroup.controls[
            'questionnaire'
          ]
            .get('thresholdAmount')
            ?.value.toString(),
          isJeRollForwardEnabled: this.pcDataService.projectConfigurationFormGroup.controls[
            'questionnaire'
          ]
            .get('isJeRollForwardEnabled')
            ?.value
        },
      },
    };
    return data;
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (this.isSaved) {
      return true;
    }

    const dialogRef = this.dialog.open(CancelConfirmationDialogComponent, {
      disableClose: true,
      width: '45%',
      data: {
        title: CommonMessages.EXIT_PROJECT_CONFIGURATION_TITLE,
        description: '',
        confirmButtonText: CommonMessages.BUTTON_TEXT_YES,
        continueButtonText: CommonMessages.BUTTON_TEXT_CANCEL,
        action: StringConstants.CONFIGURATION,
      },
    });
    return dialogRef.afterClosed();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
